import { writeFile, mkdir } from 'fs/promises';
import { dirname } from 'path';

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';

import type { BootstrapResult } from '../service/Bootstrap';
import type { ServiceRunner } from '../service/ServiceLoops';
import { computeSnapshot } from '../sync/SnapshotProtocol';

import { collectIndexingStatus, formatStatus } from './status';

const SERVER_NAME = 'coderule-scanner-mcp';
const SERVER_VERSION = process.env.npm_package_version ?? '0.0.0';

export interface CreateMcpServerOptions {
  runtime: BootstrapResult;
  runner: ServiceRunner;
}

export function createMcpServer({
  runtime,
  runner,
}: CreateMcpServerOptions): McpServer {
  async function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function isLocalStable(): boolean {
    return (
      runtime.filesRepo.countByState('dirty') === 0 &&
      runtime.filesRepo.countByState('hashing') === 0
    );
  }

  async function withDeadline<T>(
    promise: Promise<T>,
    deadlineMs: number,
  ): Promise<T> {
    const remaining = deadlineMs - Date.now();
    if (remaining <= 0) {
      throw new Error('deadline exceeded');
    }
    return Promise.race([
      promise,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('deadline exceeded')), remaining),
      ),
    ]);
  }

  const server = new McpServer({
    name: SERVER_NAME,
    version: SERVER_VERSION,
    description: 'Coderule file indexing MCP server',
  });

  server.registerTool(
    'check',
    {
      title: 'Coderule Indexing Status',
      description:
        'Check the current state of the code indexer. Returns repository info, file states, queue metrics, snapshot details, and service health. Use to debug query failures or verify indexer readiness.',
      inputSchema: {},
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async () => {
      const status = await collectIndexingStatus(runtime, runner);
      const text = formatStatus(status);
      return {
        content: [{ type: 'text', text }],
      };
    },
  );

  server.registerTool(
    'query',
    {
      title: 'Semantic Code Retrieval',
      description:
        'USE THIS before code exploration. Graph-based RAG for semantic code search using AST relationships and embeddings. Returns 30-50 code segments with file paths and line numbers. After initial query, you may query again with refined keywords or use conventional tools (Read, Glob, Grep) for specific files.',
      inputSchema: {
        query: z.string().min(1, 'Query text is required')
          .describe(`Short docstring-style description of what the code does + specific technical terms and identifiers (NOT natural language questions, NOT bare keyword lists).

STRATEGY: Start with a mini-docstring (what the code does), then append concrete identifiers, API names, error types, and domain terms.

✅ GOOD EXAMPLES:
- "Validate JWT access token and extract user claims FastAPI dependency AuthMiddleware jose jwt decode RS256 audience issuer"
- "Index repository files into embedding store Celery worker chunker AST parse imports calls graph propagate batch upsert vector_index"
- "Create Alembic migration for SQLAlchemy relationship add foreign key backfill existing rows cascade ondelete constraint naming"
- "Capture exception with traceback and structured context logging logger.exception request_id correlation_id retryable errors"
- "Load cache from file using shelve.open handle OSError fallback recreate cache file path validation"
- "Find call sites of verify_jwt decode_jwt get_current_user where used FastAPI router dependency tests pytest"

❌ BAD EXAMPLES:
- "How does authentication work?" (natural language question)
- "Show me the login code" (conversational)
- "auth" (too vague, needs more context)
- "JWT authentication middleware token validation security handler FastAPI" (bare keyword list without intent — add a docstring prefix describing what the code does)

NON-ENGLISH QUERIES: Translate to English before querying.
- "validate JWT signature and extract claims FastAPI jose RS256 issuer audience"

RETRIEVAL MECHANISM:
Combines multiple strategies: Seeds (semantic similarity), Flood (AST graph propagation through imports/calls/inheritance), Neighbors (adjacent chunks), and Calls (function relationships).

WHEN TO USE THIS TOOL:
- Before modifying ANY existing code
- Understanding system architecture
- Finding related implementations or patterns
- Locating tests or usage examples
- Bug investigation
- Feature implementation planning`),
        budgetTokens: z
          .number()
          .int()
          .positive()
          .optional()
          .describe(
            'Token budget for retrieval results. Default: 10000. Range: 1000-20000. Higher budgets return more context but use more tokens. 10k is sufficient for most tasks.',
          ),
      },
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async ({
      query,
      budgetTokens,
    }: {
      query: string;
      budgetTokens?: number;
    }) => {
      const deadline = Date.now() + runtime.config.maxQueryWaitMs;

      // Step 1: Wait for local stability (dirty=0, hashing=0)
      while (Date.now() < deadline) {
        if (isLocalStable()) break;
        await sleep(250);
      }
      if (!isLocalStable()) {
        const statusText = formatStatus(
          await collectIndexingStatus(runtime, runner),
        );
        const text = `Indexer not ready (local hashing/dirty). Current status:\n\n${statusText}`;
        return { content: [{ type: 'text', text }] };
      }

      // Step 2: Loop until server READY for current snapshot
      let currentHash = computeSnapshot(runtime.filesRepo).snapshotHash;
      let lastStatus: string | undefined;

      while (Date.now() < deadline) {
        // If local became unstable, re-wait and recompute
        if (!isLocalStable()) {
          while (Date.now() < deadline) {
            if (isLocalStable()) break;
            await sleep(250);
          }
          if (!isLocalStable()) break;
          currentHash = computeSnapshot(runtime.filesRepo).snapshotHash;
          continue;
        }

        // Check server status for currentHash
        try {
          const status = await withDeadline(
            runtime.clients.sync.checkSnapshotStatus(currentHash),
            deadline,
          );
          lastStatus = status.status;

          if (status.status === 'READY') {
            const effectiveBudget = Math.max(100, budgetTokens ?? 10000);
            try {
              const result = await withDeadline(
                runtime.clients.retrieval.query(
                  currentHash,
                  query,
                  effectiveBudget,
                  { formatter: runtime.config.retrievalFormatter },
                ),
                deadline,
              );
              return {
                content: [
                  {
                    type: 'text',
                    text: result.formatted_output ?? '(no formatted output)',
                  },
                ],
              };
            } catch (error: any) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Unknown retrieval error';
              runtime.logger.error({ err: error }, 'Retrieval query failed');
              return {
                content: [
                  { type: 'text', text: `Retrieval error: ${message}` },
                ],
                isError: true,
              };
            }
          }

          if (status.status === 'FAILED') {
            // Wait for stability and recompute (don't keep polling a FAILED hash)
            while (Date.now() < deadline) {
              if (isLocalStable()) break;
              await sleep(250);
            }
            if (!isLocalStable()) break;
            currentHash = computeSnapshot(runtime.filesRepo).snapshotHash;
            continue;
          }

          // NOT_FOUND, MISSING_CONTENT, PROCESSING: ensure publisher is triggered
          runtime.outbox.enqueueSnapshot(runtime.config.rootId, 0);
        } catch {
          // Transient error or deadline — continue loop
        }

        // Before sleeping, check if snapshot changed
        const newHash = computeSnapshot(runtime.filesRepo).snapshotHash;
        if (newHash !== currentHash) {
          currentHash = newHash;
          continue;
        }

        await sleep(500);
      }

      const statusText = formatStatus(
        await collectIndexingStatus(runtime, runner),
      );
      const text = `Snapshot not READY before deadline (last status: ${lastStatus ?? 'unknown'}). Current status:\n\n${statusText}`;
      return { content: [{ type: 'text', text }] };
    },
  );

  server.registerTool(
    'dump_bush',
    {
      title: 'Dump Raw Index (Debug)',
      description:
        'Debug tool. Downloads the raw HDF5 bush file for the current snapshot and saves it to the specified path. Returns an HDF5 bush reader skill with format documentation and Python code examples.',
      inputSchema: {
        filePath: z
          .string()
          .min(1, 'File path is required')
          .refine((p) => p.endsWith('.h5'), 'File must have .h5 extension')
          .describe(
            'Full file name to save (use base dir + any name with .h5 extension), e.g. /tmp/snapshot.h5',
          ),
      },
      annotations: {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: true,
      },
    },
    async ({ filePath }: { filePath: string }) => {
      const deadline = Date.now() + runtime.config.maxQueryWaitMs;

      // Wait for local stability
      while (Date.now() < deadline) {
        if (isLocalStable()) break;
        await sleep(250);
      }
      if (!isLocalStable()) {
        const statusText = formatStatus(
          await collectIndexingStatus(runtime, runner),
        );
        return {
          content: [
            {
              type: 'text',
              text: `Indexer not ready (local hashing/dirty). Current status:\n\n${statusText}`,
            },
          ],
          isError: true,
        };
      }

      // Wait for snapshot to be READY on the server
      let currentHash = computeSnapshot(runtime.filesRepo).snapshotHash;
      let lastStatus: string | undefined;

      while (Date.now() < deadline) {
        if (!isLocalStable()) {
          while (Date.now() < deadline) {
            if (isLocalStable()) break;
            await sleep(250);
          }
          if (!isLocalStable()) break;
          currentHash = computeSnapshot(runtime.filesRepo).snapshotHash;
          continue;
        }

        try {
          const status = await withDeadline(
            runtime.clients.sync.checkSnapshotStatus(currentHash),
            deadline,
          );
          lastStatus = status.status;

          if (status.status === 'READY') {
            try {
              // Download HDF5 bush and save to disk
              const bushBuffer = await withDeadline(
                runtime.clients.sync.downloadSnapshotBush(currentHash),
                deadline,
              );

              await mkdir(dirname(filePath), { recursive: true });
              await writeFile(filePath, Buffer.from(bushBuffer));

              runtime.logger.info(
                {
                  filePath,
                  snapshotHash: currentHash,
                  bytes: bushBuffer.byteLength,
                },
                'HDF5 bush file saved',
              );

              // Fetch the reader skill markdown
              const skill = await withDeadline(
                runtime.clients.sync.getHdf5BushReaderSkill(),
                deadline,
              );

              return {
                content: [
                  {
                    type: 'text',
                    text: `HDF5 bush saved to ${filePath} (${bushBuffer.byteLength} bytes, snapshot ${currentHash}).\n\n${skill}`,
                  },
                ],
              };
            } catch (error: any) {
              const message =
                error instanceof Error ? error.message : 'Unknown error';
              runtime.logger.error(
                { err: error, snapshotHash: currentHash },
                'Bush download failed',
              );
              return {
                content: [
                  {
                    type: 'text',
                    text: `Failed to download bush for snapshot ${currentHash}: ${message}`,
                  },
                ],
                isError: true,
              };
            }
          }

          if (status.status === 'FAILED') {
            while (Date.now() < deadline) {
              if (isLocalStable()) break;
              await sleep(250);
            }
            if (!isLocalStable()) break;
            currentHash = computeSnapshot(runtime.filesRepo).snapshotHash;
            continue;
          }

          runtime.outbox.enqueueSnapshot(runtime.config.rootId, 0);
        } catch {
          // Transient error or deadline — continue loop
        }

        const newHash = computeSnapshot(runtime.filesRepo).snapshotHash;
        if (newHash !== currentHash) {
          currentHash = newHash;
          continue;
        }

        await sleep(500);
      }

      const statusText = formatStatus(
        await collectIndexingStatus(runtime, runner),
      );
      return {
        content: [
          {
            type: 'text',
            text: `Snapshot not READY before deadline (last status: ${lastStatus ?? 'unknown'}). Current status:\n\n${statusText}`,
          },
        ],
        isError: true,
      };
    },
  );

  return server;
}
